import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../environment/environment';
import { Router } from '@angular/router';
import { clearSession, getAuthItem } from '../store/auth/auth-session';

// URLs that should NOT have agencyId or groupAdminId appended
const AGENCY_PARAM_EXCLUDE = [
    '/network-groups',
    '/auth',
    '/profile/users',
    '/profile/me',
    '/profile/public',
    '/profile/group-hierarchy',
    '/customers/pending-approval',
    '/assign',
    '/payments',
    '/service-catalog/customers',
    '/service-catalog/available',
    '/service-catalog/my-services',
    '/coupons/validate',
    '/void',
];

@Injectable()
export class ApiService {
    baseUrl = environment.apiUrl;
    imageUrl = environment.imageUrl;
    private router = inject(Router);

    constructor(private http: HttpClient) { }

    isLoggedIn(): boolean {
        const token = getAuthItem('token');
        return !!token;
    }

    getCurrentPath(): string {
        const path = this.router.url;
        return path;
    }

    private extractId(val: any): string | null {
        if (!val) return null;
        if (typeof val === 'string') return val;
        if (typeof val === 'object') {
            return val._id || val.id || null;
        }
        return null;
    }

    private getSelectedParams(url: string): { agencyId: string | null; groupAdminId: string | null } {
        const result = { agencyId: null as string | null, groupAdminId: null as string | null };
        const skip = AGENCY_PARAM_EXCLUDE.some(path => url.includes(path));
        if (skip) return result;

        try {
            const userRaw = getAuthItem('user');
            if (userRaw) {
                const user = JSON.parse(userRaw);
                const roles: string[] = Array.isArray(user?.roles) ? user.roles : (user?.role ? [user.role] : []);
                const isCustomer = roles.some((r: string) => String(r).toUpperCase() === 'CUSTOMER');
                if (isCustomer) {
                    return result;
                }
            }

            const raw = getAuthItem('selectedAgency');
            if (!raw) {
                return result;
            }
            const selected = JSON.parse(raw);

            // Extract groupAdminId
            if (selected?.groupAdminId) {
                result.groupAdminId = this.extractId(selected.groupAdminId);
            } else if (selected?.type === 'GROUP_ADMIN') {
                result.groupAdminId = this.extractId(selected?.id || selected?._id);
            }

            // Extract agencyId - do NOT send agencyId parameter when requesting /profile/agencies UNLESS type=sub-agency is present
            if (!url.includes('/profile/agencies') || url.includes('type=sub-agency')) {
                if (selected?.agencyId) {
                    result.agencyId = this.extractId(selected.agencyId);
                } else if (selected?.agency) {
                    result.agencyId = this.extractId(selected.agency);
                } else if (selected?.parentAgencyId) {
                    result.agencyId = this.extractId(selected.parentAgencyId);
                } else if (selected?.type === 'AGENCY') {
                    result.agencyId = this.extractId(selected?.id || selected?._id);
                } else if (selected?.type !== 'GROUP_ADMIN' && (selected?.id || selected?._id)) {
                    const extracted = this.extractId(selected?.id || selected?._id);
                    if (extracted && extracted !== result.groupAdminId) {
                        result.agencyId = extracted;
                    }
                }
            }
        } catch {
            return result;
        }

        return result;
    }

    private appendAgencyParam(url: string): string {
        const currentPath = this.getCurrentPath();

        const skip = AGENCY_PARAM_EXCLUDE.some(path => url.includes(path));
        if (skip) return url;

        if (currentPath.includes('/main/group-agency') && url.includes('/profile/agencies') && !url.includes('type=sub-agency')) {
            return url;
        }

        const { agencyId, groupAdminId } = this.getSelectedParams(url);
        if (!agencyId && !groupAdminId) return url;

        let updatedUrl = url;
        if (groupAdminId && !updatedUrl.includes('groupAdminId=')) {
            const separator = updatedUrl.includes('?') ? '&' : '?';
            updatedUrl += `${separator}groupAdminId=${groupAdminId}`;
        }
        if (agencyId && !updatedUrl.includes('agencyId=')) {
            const separator = updatedUrl.includes('?') ? '&' : '?';
            updatedUrl += `${separator}agencyId=${agencyId}`;
        }

        return updatedUrl;
    }

    private injectSelectedParams(formData: any, url: string, hasFile?: boolean): any {
        const { agencyId, groupAdminId } = this.getSelectedParams(url);
        if (!agencyId && !groupAdminId) return formData;

        if (hasFile) {
            const fd = formData instanceof FormData ? formData : this.prepareFormData(formData);
            if (groupAdminId && !fd.has('groupAdminId')) {
                fd.append('groupAdminId', groupAdminId);
            }
            if (agencyId && !fd.has('agencyId')) {
                fd.append('agencyId', agencyId);
            }
            return fd;
        } else {
            const extra: any = {};
            if (groupAdminId && !formData?.hasOwnProperty('groupAdminId')) extra.groupAdminId = groupAdminId;
            if (agencyId && !formData?.hasOwnProperty('agencyId')) extra.agencyId = agencyId;
            return formData ? { ...formData, ...extra } : (Object.keys(extra).length ? extra : formData);
        }
    }

    public get(url: string, formData?: any, isPublic: boolean = false, hideLoader: boolean = false): Observable<any> {
        const headers = this.buildHeaders(isPublic, hideLoader);
        const finalUrl = this.appendAgencyParam(url);
        return this.http.get(this.baseUrl + finalUrl, { headers }).pipe(
            map((response: any) => this.onResponse(response, 'response')),
            catchError((err: any) => throwError(() => this.onResponse(err, 'error')))
        );
    }

    /** GET that returns raw text (e.g. CSV export) */
    public getText(url: string, isPublic: boolean = false, hideLoader: boolean = false): Observable<string> {
        const headers = this.buildHeaders(isPublic, hideLoader);
        const finalUrl = this.appendAgencyParam(url);
        return this.http.get(this.baseUrl + finalUrl, { headers, responseType: 'text' }).pipe(
            catchError((err: any) => throwError(() => this.onResponse(err, 'error')))
        );
    }

    public delete(url: string, isPublic: boolean = false, hideLoader: boolean = false): Observable<any> {
        const headers = this.buildHeaders(isPublic, hideLoader);
        return this.http.delete(this.baseUrl + url, { headers }).pipe(
            map((response: any) => this.onResponse(response, 'response')),
            catchError((err: any) => throwError(() => this.onResponse(err, 'error')))
        );
    }

    public patch(apiSlug: string, formData?: any, isPublic: boolean = false, hideLoader: boolean = false, hasFile?: boolean): Observable<any> {
        const headers = this.buildHeaders(isPublic, hideLoader, hasFile);
        const finalUrl = this.appendAgencyParam(apiSlug);
        const data = this.injectSelectedParams(formData, apiSlug, hasFile);
        return this.http.patch(this.baseUrl + finalUrl, data, { headers }).pipe(
            map((response: any) => this.onResponse(response, 'response')),
            catchError((err: any) => throwError(() => this.onResponse(err, 'error')))
        );
    }

    public put(apiSlug: string, formData: any, isPublic: boolean = false, hideLoader: boolean = false, hasFile?: boolean): Observable<any> {
        const headers = this.buildHeaders(isPublic, hideLoader, hasFile);
        const finalUrl = this.appendAgencyParam(apiSlug);
        const data = this.injectSelectedParams(formData, apiSlug, hasFile);
        return this.http.put(this.baseUrl + finalUrl, data, { headers }).pipe(
            map((response: any) => this.onResponse(response, 'response')),
            catchError((err: any) => throwError(() => this.onResponse(err, 'error')))
        );
    }

    public getLDM(url: string, formData?: any, isPublic: boolean = false, hideLoader: boolean = false, customToken: boolean = false): Observable<any> {
        const headers = this.buildHeaders(isPublic, hideLoader, false, customToken);
        return this.http.get(this.baseUrl + url, { headers }).pipe(
            map((response: any) => this.onResponse(response, 'response')),
            catchError((err: any) => throwError(() => this.onResponse(err, 'error')))
        );
    }

    private prepareFormData(formData: any): FormData {
        // If formData is already FormData, return it as is
        if (formData instanceof FormData) {
            return formData;
        }

        const data = new FormData();
        for (const key in formData) {
            if (formData.hasOwnProperty(key)) {
                data.append(key, formData[key]);
            }
        }
        return data;
    }

    private onResponse(response: any, type: 'response' | 'error'): any {
        if (type === 'response') {
            if (response.status === 403 || response.statusCode === 403) {
                clearSession(); localStorage.clear();
                window.location.reload();
                return;
            }
            return response || response.data;
        }

        if (response.status === 403 || response.statusCode === 403) {
            clearSession(); localStorage.clear();
            window.location.reload();
            return {
                code: response.status,
                status: 'Error',
                message: 'Your session has expired',
            };
        }

        return response.error || response;
    }

    private buildHeaders(isPublic: boolean, hideLoader: boolean, hasFile?: boolean, customToken?: boolean): HttpHeaders {
        let headers = new HttpHeaders();

        if (isPublic) headers = headers.set('isPublic', 'true');
        if (hideLoader) headers = headers.set('hideLoader', 'true');

        // 🟢 Set Authorization like Axios
        const token = getAuthItem('token');
        if (token) {
            headers = headers.set('Authorization', `Bearer ${token}`);
        }

        // 🟢 Match Axios 'Content-Type'
        if (!hasFile) {
            headers = headers.set('Content-Type', 'application/json');
        }

        return headers;
    }

    public post(apiSlug: string, formData: any, isPublic: boolean = false, hideLoader: boolean = false, hasFile?: boolean): Observable<any> {
        const headers = this.buildHeaders(isPublic, hideLoader, hasFile);
        const finalUrl = this.appendAgencyParam(apiSlug);
        const data = this.injectSelectedParams(formData, apiSlug, hasFile);

        return this.http.post(this.baseUrl + finalUrl, data, { headers }).pipe(
            map((response: any) => this.onResponse(response, 'response')),
            catchError((err: any) => throwError(() => this.onResponse(err, 'error')))
        );
    }

    /** Get agencyId from localStorage only for non-excluded URLs */
    private getSelectedAgencyId(url: string): string | null {
        const skip = AGENCY_PARAM_EXCLUDE.some(path => url.includes(path));
        if (skip) return null;
        try {
            const raw = getAuthItem('selectedAgency');
            return raw ? (JSON.parse(raw)?.id || null) : null;
        } catch {
            return null;
        }
    }
}
